// ── Participants (Wave 4 — REBUILT as gold-pattern per-screen pages) ────────
// The thin contract-surface participant workspace (fixture rows + a read-only
// profile card) was replaced by real per-screen pages on the gold pattern: a
// ParticipantsPage list (DataTable + search/status/cohort filters + role-gated
// Add/Edit/Remove modals) and a ParticipantDetailPage record (identity header +
// the Access & history READ section + Edit/Complete-profile/Remove), each routed
// through useHonestQuery -> <HonestState>. The contract factories below preserve
// the route registration + provenance metadata and render the new pages via
// `customBody`.
//
// ── PARTICIPANT ISOLATION (blueprint §6 gate #1, FR-PDASH-001) ──────────────
// The admin participants module is DENIED to `participant` entirely: the route
// manifest's participants / participant-detail routes use operationsRoles
// (super_admin/admin/coach/company_admin) — excluding participant + cohort_lead
// at the route (generic-not-found + no Participants nav item) — AND both pages
// fail-close at the component (canAccessParticipantsModule) so a participant who
// reached the component sees ONLY permission-denied, never a roster or another
// participant's record. Participant own-profile context is the Wave-9
// Participant Dashboard, never this module.
//
// Coach add/import is DEFERRED to Wave 5 (the Week-3 cutoff lives there): the
// list renders Add only for super_admin/admin here.
//
// Self-edit (FR-PART-003) is scaffolding only this wave (the allowlist + guard in
// domain/participant-self-edit.ts, default read-only); the participant-FACING
// profile screen that uses it is Wave 9.

import { ParticipantsPage } from "./participants/participants-page";
import { ParticipantDetailPage } from "./participants/participant-detail-page";
import type { ProductScreenContract } from "./product-route-contracts";


function focusedDefaults(): Pick<
  ProductScreenContract,
  "requiredViewports" | "surfaceMode" | "closureState"
> {
  return {
    requiredViewports: ["desktop", "mobile"],
    surfaceMode: "focused",
    closureState: "focused",
  };
}

export function participantManagementScreen(): ProductScreenContract {
  return {
    title: () => "Participants",
    primaryJob:
      "Review every participant in scope — search, filter by status and cohort, and (for staff) add, edit, complete a profile, or remove — across the admin roster.",
    secondaryJob:
      "Company admin and cohort lead are read-only; coach sees a scoped view (add is the Wave-5 cutoff flow); participant has no participants route and never sees another participant's record.",
    workTitle: "Participant directory",
    rows: participantRows,
    columns: participantColumns(),
    states: ["loading", "empty", "permission-denied", "unavailable", "error", "read-only"],
    apiFamilies: ["participant-service", "cohort-service", "history-service"],
    roleDeltas:
      "Super admin and admin add/edit/complete/remove participants; coach is a scoped reader (add deferred to Wave 5); company_admin and cohort_lead are read-only; participant is route-denied with no Participants nav item.",
    extensionSlots: ["bulk import (Wave 5)", "Week-3 coach cutoff (Wave 5)", "self-edit allowlist (Wave 9)"],
    mobilePosture: "Participant filter bar, table rows, and lifecycle controls stack with no horizontal overflow.",
    evidenceRequirement:
      "Playwright must prove staff add/edit/complete/remove, company_admin/cohort_lead read-only, participant route-denied with no roster, access/history read, and mobile no-overflow.",
    surfaceMode: "focused",
    customBody: ParticipantsPage
  };
}

export function participantDetailScreen(): ProductScreenContract {
  return {
    ...participantManagementScreen(),
    primaryJob:
      "Inspect one participant's identity, cohort/company, status, and recorded access history before editing, completing the profile, or removing them.",
    secondaryJob:
      "The record is sourced from the scoped participant list (no detail endpoint); an out-of-scope participant resolves as not-found, never a cross-record leak.",
    workTitle: "Participant record",
    ...focusedDefaults(),
    customBody: ParticipantDetailPage
  };
}

// Representative rows for the route-contract metadata (the live list is the real
// source; this seeds the contract's `rows` so the registry test sees a non-empty
// shape). These are NOT rendered — the real pages read from the scoped services.
const participantRows = [
  {
    id: "participant-1",
    name: "Learner One",
    email: "learner@example.com",
    cohort_name: "Spring Leaders",
    company_name: "Acme Leadership",
    status: "active"
  }
];

function participantColumns() {
  return [
    { key: "name", header: "Participant" },
    { key: "email", header: "Email" },
    { key: "cohort_name", header: "Cohort" },
    { key: "company_name", header: "Company" },
    { key: "status", header: "Status" }
  ];
}
